import React, { useEffect, useState } from "react";
import { Button, Navbar, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import SyncSettings from "./SyncSettings";
import Authentication from "./Authentication";
import Confirmation from "./Confirmation";
import SubscriptionWindow from "./SubscriptionWindow";
import EnvironmentSelection from "./EnvironmentSelection";
import MSBCExtension from "./MSBCExtension";
import ExtensionCheck from "./ExtensionCheck";

import { setShowExtensionModal } from "../store/slice/preferenceSlice";

import { cmsConstructor } from "../helpers/cms/cmsConstructor";
import { useCmsHook } from "../helpers/cms/cmsHook";
import CmsEditBanner from "../helpers/cms/CmsEditBanner";

import { onChangeSettingsActiveTab } from "../store/slice/preferenceSlice";

const WINDOW = Object.freeze({
  AUTHENTICATION: "Authentication",
  SUBSCRIPTION: "Subscription",
  SYNCSETTINGS: "Sync Settings",
  CONFIRMATION: "Confirmation",
  ENVIRONMENT: "Environment",
  EXTENSION: "Extension",
  EXTENSIONCHECK: "ExtensionCheck",
});

const windowObject = {
  [WINDOW.AUTHENTICATION]: <Authentication />,
  [WINDOW.SUBSCRIPTION]: <SubscriptionWindow />,
  [WINDOW.SYNCSETTINGS]: <SyncSettings />,
  [WINDOW.CONFIRMATION]: <Confirmation />,
  [WINDOW.ENVIRONMENT]: <EnvironmentSelection />,
  [WINDOW.EXTENSION]: <MSBCExtension />,
  [WINDOW.EXTENSIONCHECK]: <ExtensionCheck />,
};

function Setup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [currentWindow, setCurrentWindow] = useState("");
  const [queryChanged, setQueryChanged] = useState(false);
  const { setCmsInstance, cmsInstance, isEdit } = useCmsHook();

  // Preference states
  const {
    settingsActiveTab,
    contactActiveTab,
    invoiceActiveTab,
    isMsbcConnected,
    isEnvPreferencesSaved,
    isMsbcExtensionInstalled,
    checkExtensionInstalled,
  } = useSelector((state) => state.preference);
  // mapping states
  const {
    isMappingsSaved,
    stripe: { loading: stripeLoading },
    msbc: { loading: msbcLoading },
  } = useSelector((state) => state.mapping);
  // contact states
  const { isContactSyncRulesSaved, isContactDefaultMappingsSaved } =
    useSelector((state) => state.contact);
  // invoice stateS
  const { isInvoiceSyncRulesSaved } = useSelector((state) => state.invoice);

  /**
   * This function is executed when user clicks on next button
   */
  const onNextNavigation = () => {
    const account = searchParams.get("account");
    if (currentWindow === WINDOW.AUTHENTICATION) {
      navigate(`${window.location.pathname}?account=${account}&env=true`);
      setCurrentWindow(WINDOW.ENVIRONMENT);
    } else if (currentWindow === WINDOW.ENVIRONMENT) {
      navigate(`${window.location.pathname}?account=${account}&extension=none`);
      setCurrentWindow(WINDOW.EXTENSIONCHECK);
    } else if (currentWindow === WINDOW.EXTENSIONCHECK) {
      if (checkExtensionInstalled) {
        navigate(
          `${window.location.pathname}?account=${account}&paymentStatus=true`
        );
        setCurrentWindow(WINDOW.SUBSCRIPTION);
      } else {
        navigate(
          `${window.location.pathname}?account=${account}&extension=none`
        );
        setCurrentWindow(WINDOW.EXTENSIONCHECK);
        dispatch(setShowExtensionModal({ show: true }));
      }
    } else if (currentWindow === WINDOW.SUBSCRIPTION) {
      navigate(
        `${window.location.pathname}?account=${account}&syncSettings=true`
      );
      setCurrentWindow(WINDOW.SYNCSETTINGS);
      dispatch(
        onChangeSettingsActiveTab({
          selectedTab: "contact",
          contactActiveTab,
          invoiceActiveTab,
          isContactSyncRulesSaved,
          isContactDefaultMappingsSaved,
          isContactMappingsSaved: isMappingsSaved.customer,
          isInvoiceSyncRulesSaved,
          isInvoiceMappingsSaved: isMappingsSaved.invoice,
          stripeLoading,
          msbcLoading,
        })
      );
    } else if (
      currentWindow === WINDOW.SYNCSETTINGS &&
      settingsActiveTab === "contact"
    ) {
      dispatch(
        onChangeSettingsActiveTab({
          selectedTab: "invoice",
          contactActiveTab,
          invoiceActiveTab,
          isContactSyncRulesSaved,
          isContactDefaultMappingsSaved,
          isContactMappingsSaved: isMappingsSaved.customer,
          isInvoiceSyncRulesSaved,
          isInvoiceMappingsSaved: isMappingsSaved.invoice,
          stripeLoading,
          msbcLoading,
        })
      );
    } else if (currentWindow === WINDOW.SYNCSETTINGS) {
      navigate(
        `${window.location.pathname}?account=${account}&confirmation=true`
      );
      setCurrentWindow(WINDOW.CONFIRMATION);
    }
  };

  /**
   * This function is executed when user clicks on back button
   */
  const onBackNavigation = () => {
    const account = searchParams.get("account");
    if (currentWindow === WINDOW.CONFIRMATION) {
      navigate(
        `${window.location.pathname}?account=${account}&syncSettings=true`
      );
      setCurrentWindow(WINDOW.SYNCSETTINGS);
      dispatch(
        onChangeSettingsActiveTab({
          selectedTab: "invoice",
          contactActiveTab,
          invoiceActiveTab,
          isContactSyncRulesSaved,
          isContactDefaultMappingsSaved,
          isContactMappingsSaved: isMappingsSaved.customer,
          isInvoiceSyncRulesSaved,
          isInvoiceMappingsSaved: isMappingsSaved.invoice,
          stripeLoading,
          msbcLoading,
        })
      );
    } else if (
      currentWindow === WINDOW.SYNCSETTINGS &&
      settingsActiveTab === "invoice"
    ) {
      dispatch(
        onChangeSettingsActiveTab({
          selectedTab: "contact",
          contactActiveTab,
          invoiceActiveTab,
          isContactSyncRulesSaved,
          isContactDefaultMappingsSaved,
          isContactMappingsSaved: isMappingsSaved.customer,
          isInvoiceSyncRulesSaved,
          isInvoiceMappingsSaved: isMappingsSaved.invoice,
          stripeLoading,
          msbcLoading,
        })
      );
    } else if (
      currentWindow === WINDOW.SYNCSETTINGS &&
      settingsActiveTab === "contact"
    ) {
      navigate(
        `${window.location.pathname}?account=${account}&paymentStatus=none`
      );
      setCurrentWindow(WINDOW.SUBSCRIPTION);
    } else if (currentWindow === WINDOW.SUBSCRIPTION) {
      navigate(`${window.location.pathname}?account=${account}&extension=true`);
      setCurrentWindow(WINDOW.EXTENSIONCHECK);
    } else if (currentWindow === WINDOW.EXTENSIONCHECK) {
      navigate(`${window.location.pathname}?account=${account}&env=true`);
      setCurrentWindow(WINDOW.ENVIRONMENT);
    } else if (currentWindow === WINDOW.EXTENSION) {
      navigate(`${window.location.pathname}?account=${account}&env=true`);
      setCurrentWindow(WINDOW.ENVIRONMENT);
    } else if (currentWindow === WINDOW.ENVIRONMENT) {
      navigate(`${window.location.pathname}?account=${account}`);
      setCurrentWindow(WINDOW.AUTHENTICATION);
    }
  };

  /**
   * This function is executed when user clicks on finish button
   */
  const goBackToStripe = () => {
    window.open(`https://dashboard.stripe.com/dashboard`, "_self");
  };

  /**
   * This function is used for getting rendering the components while initial loading
   */
  const renderComponents = () => {
    const account = searchParams.get("account");
    const paymentStatus = searchParams.get("paymentStatus");
    const syncSettings = searchParams.get("syncSettings");
    const confirmation = searchParams.get("confirmation");
    const env = searchParams.get("env");
    const extension = searchParams.get("extension");
    if (account && confirmation) {
      setCurrentWindow(WINDOW.CONFIRMATION);
    } else if (account && paymentStatus) {
      setCurrentWindow(WINDOW.SUBSCRIPTION);
    } else if (account && syncSettings) {
      setCurrentWindow(WINDOW.SYNCSETTINGS);
    } else if (account && env) {
      setCurrentWindow(WINDOW.ENVIRONMENT);
    } else if (account && extension) {
      setCurrentWindow(WINDOW.EXTENSIONCHECK);
    } else if (account) {
      setCurrentWindow(WINDOW.AUTHENTICATION);
    }
  };

  useEffect(() => {
    renderComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryChanged]);

  useEffect(() => {
    const accountId = searchParams.get("account_id");
    if (accountId) {
      navigate(`${window.location.pathname}?account=${accountId}`);
      setQueryChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCmsInstance = async () => {
      const cmsObj = await cmsConstructor();
      setCmsInstance(cmsObj);
    };
    fetchCmsInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Navbar fixed="top" className="navbar py-2">
        <div>
          {currentWindow === WINDOW.AUTHENTICATION ? (
            <p className="hide-button"></p>
          ) : (
            <Button onClick={onBackNavigation} className="navbar-button ms-3">
              Back
            </Button>
          )}
        </div>
        <div className="text-center header">
          {/* <strong className="navbar-heading">Stripe - Business Central</strong>
          <br></br>
          <span className="navbar-subheading">
            Cloudify -
            <a
              href="https://www.cloudify.biz/contact"
              target="_blank"
              rel="noreferrer"
              className="navbar-subheading-link"
            >
              {" "}
              contact us
              <HiExternalLink />
            </a>
          </span> */}
          {cmsInstance && cmsInstance.showRichText("cmsContent.header")}
        </div>
        <div>
          {currentWindow === WINDOW.CONFIRMATION ? (
            <Button onClick={goBackToStripe} className="navbar-button me-3">
              Finish
            </Button>
          ) : (
            <>
              {(currentWindow === WINDOW.AUTHENTICATION && !isMsbcConnected) ||
              (currentWindow === WINDOW.ENVIRONMENT &&
                !isEnvPreferencesSaved) ||
              // (currentWindow === WINDOW.EXTENSIONCHECK &&
              //   !checkExtensionInstalled) ||
              (currentWindow === WINDOW.SYNCSETTINGS &&
                settingsActiveTab === "contact" &&
                (!isMappingsSaved.customer ||
                  !isContactSyncRulesSaved ||
                  !isContactDefaultMappingsSaved)) ||
              (settingsActiveTab === "invoice" &&
                (!isMappingsSaved.invoice || !isInvoiceSyncRulesSaved)) ? (
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip style={{ fontSize: "11px" }}>
                      {showTooltipText(
                        currentWindow,
                        isMappingsSaved.customer,
                        isMappingsSaved.invoice,
                        isMsbcConnected,
                        isContactSyncRulesSaved,
                        isContactDefaultMappingsSaved,
                        isInvoiceSyncRulesSaved,
                        isEnvPreferencesSaved,
                        isMsbcExtensionInstalled,
                        checkExtensionInstalled
                      )}
                    </Tooltip>
                  }
                >
                  <Button
                    style={{ opacity: "0.7", cursor: "not-allowed" }}
                    className="navbar-button me-3"
                  >
                    Next
                  </Button>
                </OverlayTrigger>
              ) : (
                <Button
                  onClick={onNextNavigation}
                  className="navbar-button me-3"
                >
                  Next
                </Button>
              )}
            </>
          )}
        </div>
      </Navbar>

      <div className="mt-5">
        <div
          className="d-flex flex-row justify-content-center"
          style={{
            backgroundColor: "#f6f8fa",
            marginTop: `${
              isEdit && window.location.pathname === "/setup" ? "150px" : "0px"
            }`,
          }}
        >
          <CmsEditBanner />
        </div>
        <div>{windowObject[currentWindow]}</div>
      </div>
    </div>
  );
}

export default Setup;

/**
 * This function is responsible for showing custom message in tooltip
 * @returns custom message
 */
function showTooltipText(
  currentWindow,
  isCustomerMappingsSaved,
  isInvoiceMappingsSaved,
  isMsbcConnected,
  isContactSyncRulesSaved,
  isContactDefaultMappingsSaved,
  isInvoiceSyncRulesSaved,
  isEnvPreferencesSaved,
  isMsbcExtensionInstalled,
  checkExtensionInstalled
) {
  if (currentWindow === WINDOW.SYNCSETTINGS) {
    if (!isCustomerMappingsSaved) {
      return "Please save the contact mappings before proceeding further";
    }
    if (!isInvoiceMappingsSaved) {
      return "Please save the invoice mappings before proceeding further";
    }
    if (!isContactSyncRulesSaved) {
      return "Please save the contact sync rules, before proceeding further";
    }
    if (!isContactDefaultMappingsSaved) {
      return "Please save the contact default mappings, before proceeding further";
    }
    if (!isInvoiceSyncRulesSaved) {
      return "Please save the invoice sync rules before proceeding further";
    }
  } else if (currentWindow === WINDOW.AUTHENTICATION && !isMsbcConnected) {
    return "Please Connect Business Central before proceeding further";
  } else if (currentWindow === WINDOW.ENVIRONMENT && !isEnvPreferencesSaved) {
    return "Please save the preferences before proceeding further";
  } else if (
    currentWindow === WINDOW.EXTENSIONCHECK &&
    !checkExtensionInstalled
  ) {
    return "Please install Business Central Extension before proceeding further";
  }
}
